import Axios from 'helpers/Interceptor';
import { BrandContent, CampaignMetadata, TourDate, WebsiteSocialMediaIcon } from 'modules/Types';
import { WebsiteType } from 'modules/Const';

/** Interface for external links in website content */
interface ExternalLink {
    /** Name/title of the link */
    name: string;
    /** URL the link points to */
    url: string;
    /** Whether the link is enabled */
    enabled: boolean;
    /** Type of link (e.g. 'spotify', 'apple_music', etc) */
    type: string;
}

/** Interface for website style configuration */
interface WebsiteStyle {
    titleText: string;
    showSecondaryLine: boolean;
    subtitle: string;
    secondaryColor: string;
    primaryColor: string;
    titleAlignment: string;
    submitButtonText: string;
    previewUrl?: string;
    bgType: string;
    bgColor?: string;
    font: string;
    template: string;
    youtubeEmbed: { id: string; url: string; } | null;
    buttonConfig: string;
    enableSocialIcons: boolean;
    selectedPreviewTrack: string;
    full_video_url: string;
    customThumbnailViewStyle: string;
}

/** Interface for website content configuration */
interface WebsiteContent {
    fields: any[];
    tourDates: TourDate[] | null;
    hidePastTourDates: boolean;
    post_subscribe_message: string;
    style: WebsiteStyle;
}

/** Interface for the formatted website data */
export interface FormattedWebsiteData {
    /** Unique identifier for the content */
    id?: number;
    /** Type of website */
    type: WebsiteType;
    /** Content metadata including website configuration */
    content_metadata: {
        name: string;
        external_links?: ExternalLink[];
        website: WebsiteContent;
        [key: string]: any;
    };
    /** ID of the associated brand */
    brand_id: number;
    /** URL slug for the website */
    slug: string;
    /** Name of the website/content */
    name: string;
    /** Full URL of the website */
    url: string;
}

type BrandWebsiteData = {
    buttonConfig?: string,
    enableSocialIcons?: boolean,
    removeSymphonyBrand?: boolean,
    socialIcons?: WebsiteSocialMediaIcon[],
}

/**
 * Saves website configuration for a brand
 * @async
 * @param {string} brandSlug - The brand's unique slug
 * @param {BrandWebsiteData} websiteData - Website configuration data
 * @throws {Error} If the API request fails
 */
export const saveBrandWebsite_api = async (brandSlug: string, websiteData: BrandWebsiteData) => {
    try {
        const response = await Axios.post(`/website/${brandSlug}`, websiteData);
        return response.data;
    } catch (error) {
        console.error('error, saveBrandWebsite', error);
        throw error;
    }
}

/**
 * Updates website content for a specific brand and website
 * @async
 * @param {string} brandSlug - The brand's unique slug
 * @param {FormattedWebsiteData} websiteData - Updated website content
 * @throws {Error} If the API request fails
 */
export const saveWebsiteChanges_api = async (brandSlug: string, websiteData: FormattedWebsiteData) => {
    try {
        const response = await Axios.post(`/website/update/${brandSlug}/${websiteData.slug}`, websiteData);
        return response.data;
    } catch (error) {
        console.error('error, saveWebsiteChanges', error);
        throw error;
    }
}

/**
 * Saves changes to a website for a specific brand and content
 * @async
 * @param {string | number} slug - The unique identifier for the brand content
 * @returns {Promise<Campaign>} The response data from the API
 * @throws {Error} If the API request fails
 * @description Makes a POST request to update website content for a specific brand and content slug
 */

export const boostWebsite_api = async (slug: number | string): Promise<{ id: String; campaign_metadata: CampaignMetadata }> => {
    try {
        const response = await Axios.post<{ id: String; campaign_metadata: CampaignMetadata }>(`/website/boost/${slug}`);
        return response.data;
    } catch (error) {
        console.log('error, boostWebsite', error);
        throw error;
    }
}

/**
 * Retrieves collected data for a specific website
 * @async
 * @param {string} slug - The unique identifier for the website
 * @returns {Promise<UserResponse[]>} Array of user responses collected from the website
 * @throws {Error} If the API request fails
 */
export const getWebsiteData_api = async (slug: string) => {
    try {
        const response = await Axios.get(`/website/${slug}/data`);
        return response.data;
    } catch (error) {
        console.error('error, getWebsiteData', error);
        throw error;
    }
}

/**
 * Downloads collected data as CSV for a specific website
 * @async
 * @param {number} contentId - The ID of the website content
 * @returns {Promise<string>} URL to download the CSV file
 * @throws {Error} If the API request fails
 */
export const downloadWebsiteDataCSV_api = async (contentId: number) => {
    try {
        const response = await Axios.post(`/website/${contentId}/csv/collected-data`);
        return response.data;
    } catch (error) {
        console.error('error, downloadWebsiteDataCSV', error);
        throw error;
    }
}

/**
 * Retrieves tour dates for a specific artist from BandsInTown
 * @async
 * @param {string} artist - The artist name to fetch tour dates for
 * @returns {Promise<{bandsintownTourData: Array<{
 *   artist: string;
 *   venue: string;
 *   city: string;
 *   country: string;
 *   date: string;
 *   ticketUrl: string;
 * }>, artist: {name: string}}>} The tour dates data from BandsInTown
 * @throws {Error} If the API request fails
 */
export const getBandsInTownTourDates_api = async (artist: string) => {
    try {
        const response = await Axios.get<{
            bandsintownTourData: any,
            artist: { name: string }
        }>(`/website/bandsintown/tour-dates?artist=${encodeURIComponent(artist)}`);
        return response.data;
    } catch (error) {
        console.error('error, getBandsInTownTourDates', error);
        throw error;
    }
}

interface TourDatesResponse {
    tour_dates: {
        dateArray: TourDate[];
    };
}
/**
 * Uploads and processes a CSV file containing tour dates
 * @async
 * @param {FormData} formData - Form data containing the CSV file to upload
 * @returns {Promise<TourDatesResponse>} Response containing processed tour dates array and any errors
 * @throws {Error} If the API request fails or CSV processing fails
 */
export const downloadTourDatesCsv_api = async (formData: FormData) => {
    try {
        const response = await Axios.post<TourDatesResponse>('/website/csv/tour-dates', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('error, downloadTourDatesCsv', error);
        throw error;
    }
}

export const processTourDatesText_api = async (text: string) => {
    try {
        const response = await Axios.post<TourDatesResponse>('/website/text/tour-dates', { text });
        return response.data;
    } catch (error) {
        console.error('error, processTourDatesText', error);
        throw error;
    }
}

/**
 * Updates the URL/slug of a website
 * @async
 * @param {Object} fansImportData - The data containing the new URL information
 * @param {string} fansImportData.url - The new URL/slug for the website
 * @param {string} fansImportData.contentId - The ID of the website content
 * @param {number} fansImportData.campaignId - The ID of the associated campaign
 * @returns {Promise<any>} The response data from the API
 * @throws {Error} If the API request fails
 */
export const updateWebsiteUrl_api = async (data: { url: string;
    contentId?: number;
    campaignId?: number;
    contentSlug?: string;
}): Promise<BrandContent> => {
    try {
        const response = await Axios.put('/website/url', data);
        return response.data
    } catch (error) {
        console.error('error, updateWebsiteUrl', error);
        throw error;
    }
}

/**
 * Retrieves active websites with optional parameters
 * @async
 * @param {Object} params - Query parameters
 * @param {string[]} params.brandContentsTypes - Array of content types to filter by
 * @param {boolean} params.attachCampaigns - Whether to include campaign data
 * @returns {Promise<any>} The response data containing active websites
 * @throws {Error} If the API request fails
 */
export const getWebsites_api = async (params: { brandContentsTypes: string[]; attachCampaigns: boolean }) => {
    try {
        const response = await Axios.get('/website', { params });
        return response.data;
    } catch (error) {
        console.error('error, getActiveWebsites', error);
        throw error;
    }
}
